import React, { useState } from "react";
import {
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import BaseModal from "../modals/BaseModal";

/**
 * Attachment building blocks shared by the task's Attachments section and
 * the comments in the activity panel. Files live in the public Supabase
 * bucket; the list itself is only returned to users who can see the task.
 */

export const ACCEPTED_FILES = ".xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp,.pdf";
export const MAX_FILE_BYTES = 10 * 1024 * 1024; // mirrors the API default
const ACCEPTED_EXT = ACCEPTED_FILES.split(",");

export const isImage = (a) => a?.mimeType?.startsWith("image/");

export const formatSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const typeLabel = (a) => (a.originalName.split(".").pop() || "").toUpperCase();

/** Client-side pre-check (the server validates the metadata again). */
export const checkFile = (file) => {
  const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
  if (!ACCEPTED_EXT.includes(ext)) return `${file.name}: unsupported file type`;
  if (file.size > MAX_FILE_BYTES)
    return `${file.name}: larger than ${formatSize(MAX_FILE_BYTES)}`;
  if (!file.size) return `${file.name}: file is empty`;
  return null;
};

/** Image served from its (public) storage URL. */
export const AuthImage = ({ attachment, className, alt }) => {
  const [failed, setFailed] = useState(false);
  if (failed || !attachment.url) return <ImageIcon className="w-6 h-6 text-gray-400" />;
  return (
    <img
      src={attachment.url}
      alt={alt || attachment.originalName}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

/** Save a file (falls back to opening it when the browser blocks the download). */
export const downloadAttachment = async (attachment) => {
  try {
    const res = await fetch(attachment.url);
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = attachment.originalName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    window.open(attachment.url, "_blank", "noopener,noreferrer");
  }
};

const FileIcon = ({ attachment }) => {
  const Icon = /sheet|excel|csv/.test(attachment.mimeType)
    ? FileSpreadsheet
    : FileText;
  return <Icon className="w-6 h-6 text-gray-500 dark:text-light-400" />;
};

const iconButton =
  "p-1.5 rounded-md text-gray-500 hover:text-primary-accent hover:bg-primary-accent/10 disabled:opacity-50";

/** One file: thumbnail/icon, name, type, size and actions. */
export const AttachmentTile = ({
  attachment,
  canRemove,
  onRemove,
  onPreview,
  onError,
  compact = false,
}) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      onError?.(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3 min-w-0 p-2 rounded-lg border border-gray-200 dark:border-dark-100 bg-white dark:bg-dark-400">
      <button
        type="button"
        onClick={() =>
          isImage(attachment)
            ? onPreview?.(attachment)
            : run(() => downloadAttachment(attachment))
        }
        className={`shrink-0 flex items-center justify-center overflow-hidden rounded-md bg-gray-100 dark:bg-dark-300 ${
          compact ? "w-10 h-10" : "w-16 h-12"
        }`}
        aria-label={
          isImage(attachment)
            ? `Preview ${attachment.originalName}`
            : `Download ${attachment.originalName}`
        }
      >
        {isImage(attachment) ? (
          <AuthImage
            attachment={attachment}
            className="w-full h-full object-cover"
          />
        ) : (
          <FileIcon attachment={attachment} />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-medium text-gray-900 dark:text-light-100 truncate"
          title={attachment.originalName}
        >
          {attachment.originalName}
        </p>
        <p className="text-xs text-gray-500 dark:text-light-500">
          {typeLabel(attachment)} · {formatSize(attachment.size)}
        </p>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {isImage(attachment) && (
          <button
            type="button"
            className={iconButton}
            onClick={() => onPreview?.(attachment)}
            aria-label="Preview"
            title="Preview"
          >
            <Eye className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          className={iconButton}
          disabled={busy}
          onClick={() => run(() => downloadAttachment(attachment))}
          aria-label={`Download ${attachment.originalName}`}
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>
        {canRemove && (
          <button
            type="button"
            className={`${iconButton} hover:!text-red-600 hover:!bg-red-50 dark:hover:!bg-red-900/20`}
            disabled={busy}
            onClick={() => run(() => onRemove(attachment))}
            aria-label={`Remove ${attachment.originalName}`}
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export const ImagePreviewModal = ({ attachment, onClose }) => (
  <BaseModal
    isOpen={Boolean(attachment)}
    onClose={onClose}
    size="xl"
    elevated
    portal
    title={attachment?.originalName || ""}
    subtitle={attachment ? formatSize(attachment.size) : ""}
  >
    {attachment && (
      <div className="flex justify-center">
        <AuthImage
          attachment={attachment}
          className="max-h-[70vh] max-w-full rounded-lg object-contain"
        />
      </div>
    )}
  </BaseModal>
);
